import { utils } from '@digital-commerce/shared-utils'
import {
  allPass,
  complement,
  equals,
  evolve,
  filter,
  gte,
  includes,
  map,
  modulo,
  path,
  pipe,
  prop,
  propOr,
  __,
  isEmpty,
  ifElse,
  always,
  unnest,
  reject,
  sum,
  pathOr,
  anyPass,
  propEq,
  isNil,
  values,
  clone,
  pathEq,
  set,
  lensPath,
  findIndex,
  find,
  memoizeWith
} from 'ramda'
import {
  filterExistingActions,
  getFirstMonthItemPrice,
  getItemWithTags,
  groupMonthlyItemsByOfferId,
  isAppointmentRequired,
  getCacheKey
} from '../../../../utils/cart'
import { formatCurrency } from '../../../../utils/currency'
import { buildHSNonRecurringDiscounts, getDiscountDisplayName } from '../../../../utils/discount'
import { getCheckoutDescription } from '../../../molecules/CartItem/utils'
import { buildHSCreditList } from '../../../../utils/display'
import {
  formatHSEppDiscount,
  getHSEppDiscount,
  getServiceWithoutSpecifiedDiscounts,
  isHSEppDiscount
} from '../../../../utils/homeSolutions'

const {
  getHSService,
  getDisplayName,
  getMonthlyItemPrice,
  findMonthlyRecurring,
  getMonthlyDiscounts,
  isBusinessCart
} = utils.cart

const SMARTWEAR_CHANGE_URL = '/shop/home-services/smartwear/safety-jewelry-devices'
const SHS_CHANGE_URL = '/shop/home-services/customize'

/**
 * Gets HS service with "security" and "service-plan" in item tags
 * @param {object} cart Cart response
 * @return {object} Evolved top-level security item.
 * Within this object, the items property contains an array of nested objects
 * of other items in relation to this offer.
 */
export const getSecurityService = memoizeWith(getCacheKey('HS-Security'), (cart) => {
  return pipe(
    getHSService(['security']),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

export const isSecurityItem = pipe(
  prop('tags'),
  allPass([includes('service-plan'), includes('security')])
)

export const hasSmartHomeSecurity = (service, cart) => {
  if (!service || isBusinessCart(cart)) return false
  if (!isSecurityItem(service)) return false
  return true
}

const isSHS = pipe(
  prop('tags'),
  allPass([
    includes('service-plan'),
    includes('security'),
    ifElse(includes('smartwear'), always(false), always(true)),
    ifElse(includes('smartwear-device'), always(false), always(true))
  ])
)

const isSWS = pipe(
  prop('tags'),
  allPass([includes('service-plan'), includes('security'), includes('smartwear')])
)

export const getSubHeading = (service) => {
  const isSmartwear = isSWS(service)
  return isSmartwear ? 'smartwearSecurity' : 'smartHomeSecurity'
}

export const getChangeUrl = (service) => {
  const isSmartwear = isSWS(service)
  return isSmartwear ? SMARTWEAR_CHANGE_URL : SHS_CHANGE_URL
}

const getInstallationType = (cart, translate) => {
  const isProInstall = isAppointmentRequired(cart)

  return isProInstall ? translate('item.proInstall') : translate('item.selfInstall')
}

/**
 * Extract all additional devices from a service
 * @param {object} service
 * @returns {[object]} Array of objects of additional device items
 */
const getAdditionalHardware = (service) =>
  pipe(
    pathOr([], ['items']),
    getItemWithTags(['hardware']),
    filter((item) => getMonthlyItemPrice(item).amount > 0)
  )(service)

const getAdditionalDevicesQuantity = pipe(getAdditionalHardware, map(path(['quantity'])), sum)

const getDevices = pipe(
  propOr([], 'items'),
  filter(pipe(prop('tags'), anyPass([includes('smartwear-device'), includes('hardware')])))
)

const getReplacedAttributes = (contentAttributes, service, installationType) => {
  const additionalDevicesQty = isSHS(service)
    ? getAdditionalDevicesQuantity(service)
    : getDevices(service).length
  const attributesString = contentAttributes
    .join('__')
    .replace('{0}', installationType)
    .replace('{1}', additionalDevicesQty)
  const shsAttributes = attributesString.split('__')

  if (additionalDevicesQty === 0) shsAttributes.pop()
  return shsAttributes
}

export const getOverriddenAttributes = (service, cart, translate) => {
  const contentAttributes = pipe(unnest, reject(isEmpty))([getCheckoutDescription(service)])
  const installationType = getInstallationType(cart, translate)
  const attributes = getReplacedAttributes(contentAttributes, service, installationType)
  return attributes
}

export const getIncludedHardware = (service) =>
  pipe(
    pathOr([], ['items']),
    getItemWithTags(['hardware']),
    filter((item) => getMonthlyItemPrice(item).amount === 0)
  )(service)

const getGroupedIncludedHardware = pipe(getIncludedHardware, groupMonthlyItemsByOfferId)

export const buildSubItemsList = (subItems, lang, isMonthly = true, showQuantity = true) => {
  const mapper = (featureItem) => {
    const suffix = isMonthly ? '/mo.' : ''
    const name = getDisplayName(featureItem)
    const price = `${formatCurrency(getMonthlyItemPrice(featureItem).subTotal, lang)}${suffix}`
    const quantity = showQuantity ? `${featureItem?.quantity} ` : ''

    return {
      id: featureItem.id,
      name: `${quantity}${name}`,
      price
    }
  }

  return subItems?.map(mapper)
}

export const buildIncludedDevicesList = (service, lang) => {
  return buildSubItemsList(getGroupedIncludedHardware(service), lang)
}

const getGroupedMonthlyAdditionalDevices = pipe(
  getDevices,
  filter(
    pipe(
      path(['itemPrice', 'recurring']),
      findMonthlyRecurring,
      allPass([
        complement(isEmpty),
        complement(propEq('recurringDuration', 1)),
        complement(propEq('amount', 0))
      ])
    )
  ),
  groupMonthlyItemsByOfferId
)

export const buildAdditionalDevicesList = (service, lang) =>
  buildSubItemsList(getGroupedMonthlyAdditionalDevices(service), lang)

export const buildRecurringDiscountsList = (service, lang, translate, isAffiliates) => {
  // Business rule: EPP discount must be displayed as a separate line item, and is displayed last
  const eppDiscount = getHSEppDiscount(service)
  const serviceWithoutEppDiscount = getServiceWithoutSpecifiedDiscounts(service, [eppDiscount])
  const monthlyDiscounts = getMonthlyDiscounts(serviceWithoutEppDiscount)
  const filteredMonthlyDiscounts = monthlyDiscounts.filter(
    pipe(prop('duration'), allPass([gte(__, 1), pipe(modulo(__, 12), equals(0))]))
  )

  const formattedMonthlyDiscounts = filteredMonthlyDiscounts?.map((discount, index) => ({
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    id: `${index}-${getDiscountDisplayName(discount)}`,
    prefixIcon: 'DEALS'
  }))

  if (eppDiscount)
    formattedMonthlyDiscounts?.push(formatHSEppDiscount(eppDiscount, translate, lang, isAffiliates))

  return formattedMonthlyDiscounts
}

const _durationIs0 = filter(pipe(prop('recurringDuration'), equals(__, 0)))

const findSWSDiscounts = pipe(
  path(['itemPrice', 'recurring']),
  findMonthlyRecurring,
  propOr([], 'alterations'),
  filterExistingActions,
  _durationIs0,
  find(
    pipe(
      prop('tags'),
      allPass([
        ifElse(isNil, always(false), always(true)),
        includes('security'),
        includes('discount')
      ])
    )
  )
)

const getSWSDiscounts = (resources) => {
  return isSWS(resources) && findSWSDiscounts(resources)
}

export const buildSWSRecurringDiscount = (service, lang) => {
  const discount = getSWSDiscounts(service)

  if (!discount) return null

  return {
    name: discount.name,
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    id: discount.name,
    prefixIcon: 'DEALS'
  }
}

const getAdditionalCharges = (cart) =>
  pipe(path(['items']), getItemWithTags(['security', 'fee']), values)(cart)

export const buildAdditionalChargesList = (cart, lang) => {
  return buildSubItemsList(getAdditionalCharges(cart), lang, false, false)
}

const findMonthlyRecurringIndex = (recurring) =>
  findIndex(
    allPass([
      pathEq(['recurringPeriod', 'every'], 1),
      pathEq(['recurringPeriod', 'unit'], 'MONTH'),
      complement(propEq('recurringDuration', 1))
    ])
  )(recurring)

const findFirstMonthIndex = (recurring) =>
  findIndex(
    allPass([
      pathEq(['recurringPeriod', 'every'], 1),
      pathEq(['recurringPeriod', 'unit'], 'MONTH'),
      propEq('recurringDuration', 1)
    ])
  )(recurring)

const updatePriceforExistingItem = (existingItem, item, isMonthlyPayment) => {
  const itemPrice = isMonthlyPayment
    ? getMonthlyItemPrice(item).amount
    : getFirstMonthItemPrice(item).amount
  const itemPriceIndex = isMonthlyPayment
    ? findMonthlyRecurringIndex(existingItem.itemPrice.recurring)
    : findFirstMonthIndex(existingItem.itemPrice.recurring)
  const existingItemPrice = existingItem.itemPrice.recurring[itemPriceIndex]
  if (existingItemPrice) {
    existingItemPrice.amount += existingItemPrice ? itemPrice : 0
    const monthlyItemPrice = lensPath(['recurring', itemPriceIndex])
    set(monthlyItemPrice, existingItemPrice, existingItem.itemPrice.recurring)
  }
}

const groupItemsOfferId = (items, isMonthlyPayment) => {
  const itemsByKey = {}
  const clonedItems = clone(items)
  clonedItems.forEach((item) => {
    const existingItem = itemsByKey[item.productOffering.id]
    if (existingItem) {
      updatePriceforExistingItem(existingItem, item, isMonthlyPayment)
      existingItem.quantity += 1
    } else {
      itemsByKey[item.productOffering.id] = item
    }
  })
  return Object.values(itemsByKey)
}

const groupFirstMonthItemsByOfferId = (items) => {
  return groupItemsOfferId(items, false)
}

const getFirstMonthAdditionalDevices = pipe(
  getDevices,
  filter(
    pipe(
      path(['itemPrice', 'recurring']),
      findMonthlyRecurring,
      allPass([
        complement(isEmpty),
        propEq('recurringDuration', 1),
        complement(propEq('amount', 0))
      ])
    )
  ),
  groupFirstMonthItemsByOfferId
)

export const buildOneTimeAdditionalDevicesList = (service, lang) => {
  return buildSubItemsList(getFirstMonthAdditionalDevices(service), lang, false)
}

const calculateSumOfPriceForFirstMonthAdditionalDevices = (firstMonthAdditionalDevices) => {
  return firstMonthAdditionalDevices.reduce(
    (accum, device) => accum + device.itemPrice.recurring[0].amount,
    0
  )
}

export const getTotalPriceForFirstMonthAdditionalDevices = (oneTimeDevices, service) => {
  if (isNil(oneTimeDevices) || isEmpty(oneTimeDevices)) return undefined
  return calculateSumOfPriceForFirstMonthAdditionalDevices(getFirstMonthAdditionalDevices(service))
}

export const buildNonRecurringDiscountAndCreditList = (service, cart, lang, translate, isAffiliates) => {
  const nonRecurringDiscounts = buildHSNonRecurringDiscounts([service], lang)
  const credits = buildHSCreditList('security', cart, lang)

  // Business rule: EPP credit is displayed last
  const eppCredit = credits?.find((credit) => isHSEppDiscount(credit))
  if (eppCredit) {
    const idx = credits?.indexOf(eppCredit)
    credits?.splice(idx, 1)
    const formattedEppCredit = {
      ...eppCredit,
      name: isAffiliates? translate('priceBreakdown.affiliateCreditLabel'): translate('priceBreakdown.eppCreditLabel')
    }
    credits?.push(formattedEppCredit)
  }

  return [...nonRecurringDiscounts, ...credits]
}

export const shouldRender = (cart) => {
  const service = getSecurityService(cart)
  return hasSmartHomeSecurity(service, cart)
}
